// Lucian UX/UI Studio #914 · http://10.1.10.106:5176/#914
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from '../../icons';
import { getDateLocale } from '../../lib/base-i18n';
import { tCalendar } from '../../lib/calendar-i18n';
import { displayHour12, pad2 as pad } from './time-label';
import './EventRangePicker.css';

/**
 * ⭐ 2026-10-20 — 일정 창 시작·종료 고르개. 원본 #914 「연도 팝업·드럼 시간 선택 캘린더」를 옮겼다(사용자 지시).
 *   같은 날 먼저 넣은 #903(시간을 글자로 입력)을 이것으로 바꿨다 — 시간은 «드럼을 굴려» 고른다.
 *
 * 일정 창에는 「날짜」 상자 하나(시안 B · 아래 trigger)가 있고, 시작·종료 어느 덩어리를 눌러도 «같은 달력 창» 하나가 뜬다.
 *   - 달력 창 위쪽 시작/종료 칸: 지금 고르는 칸이 강조된다. 하루종일이 아니면 칸 오른쪽에 작은 시간이 보이고,
 *     그것을 누르면 옆 말풍선에서 오전·오후 / 시 / 분(5분 단위) 드럼으로 고른다.
 *   - 달력: 시작 → 종료 순서로 번갈아 채운다. 종료를 고를 때는 마우스를 올린 날까지 띠를 미리 보인다.
 *     시작보다 앞 날을 종료로 고르면 둘을 바꾼다(원본 규칙). 연월 제목을 누르면 연도 팝업(12개)이 열린다.
 *   - 옵션: 「하루종일」(원본 「시간 포함」의 반대 뜻) · 「시간 형식」 12/24시간 · 맨 아래 「확인」.
 *
 * 값은 종전 그대로 'YYYY-MM-DDTHH:mm' 두 문자열이다 — 부모의 저장·검증 동작은 바꾸지 않는다.
 *   시작을 바꾸면 onStartChange(부모 = 길이를 지켜 종료도 옮김), 종료는 onEndChange 를 부른다.
 *   하루종일일 때는 지금 값의 시각(부모가 붙인 하루종일 시각)을 그대로 둔다.
 */
interface EventRangePickerProps {
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onAllDayChange: (checked: boolean) => void;
  accent?: string;
  required?: boolean;
  /** 종료 칸 감싸개에 붙일 클래스(부모의 「여기를 고치세요」 표시). */
  endFieldClassName?: string;
  /**
   * ⭐ 2026-10-20 — 유형별 추천 칩 줄(시안 E · type-time-presets.ts). 시작·종료 칸 바로 아래에 그린다. 없으면 줄이 없다.
   */
  presets?: { label: string; chips: Array<{ key: string; label: string; on: boolean; onPick: () => void }> } | null;
  /** ⭐ 2026-10-20 — 휴가 반차·반반차: 종료는 «시작 + 길이»로 정해져 고를 수 없다(흐리게 · 달력은 늘 시작을 고른다). */
  endLocked?: boolean;
}

type Key = 's' | 'e';

const DAY = 864e5;
const EASE = 'cubic-bezier(.2,.8,.2,1)';
const IH = 26; // 드럼 한 줄 높이(px) — CSS .u914-col li 와 같다
const YMIN = 1900, YMAX = 2199;
const H24_PREF = 'calendar.picker.h24';
const dn = (y: number, m0: number, d: number) => Math.round(Date.UTC(y, m0, d) / DAY);
const pt = (n: number) => {
  const t = new Date(n * DAY);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), w: t.getUTCDay() };
};
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** 'YYYY-MM-DDTHH:mm' → 날짜 번호 · 분. 읽지 못하면 오늘 0분. */
function read(v: string): { n: number; min: number } {
  const [d, t] = (v ?? '').split('T');
  const [y, mo, day] = (d ?? '').split('-').map(Number);
  const [h, m] = (t ?? '').split(':').map(Number);
  if (!y || !mo || !day) {
    const now = new Date();
    return { n: dn(now.getFullYear(), now.getMonth(), now.getDate()), min: 0 };
  }
  return { n: dn(y, mo - 1, day), min: (h || 0) * 60 + (m || 0) };
}
function write(n: number, min: number): string {
  const o = pt(n);
  return `${o.y}-${pad(o.m)}-${pad(o.d)}T${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}
const localDate = (n: number) => { const o = pt(n); return new Date(o.y, o.m - 1, o.d); };

function readH24(): boolean {
  try { return window.localStorage.getItem(H24_PREF) === '1'; } catch { return false; }
}
function saveH24(v: boolean) {
  try { window.localStorage.setItem(H24_PREF, v ? '1' : '0'); } catch { /* 저장 못 해도 이번 창에서는 그대로 쓴다 */ }
}
function reducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}
function play(el: Element | null | undefined, frames: Keyframe[], ms: number) {
  if (!el || reducedMotion() || typeof (el as HTMLElement).animate !== 'function') return;
  (el as HTMLElement).animate(frames, { duration: ms, easing: EASE });
}

/** 드럼: 가운데에서 멀수록 원통 뒤로 넘어가듯 기울인다(원본 bend). li 의 style 은 React 가 쥐지 않는다. */
function bend(c: HTMLElement) {
  const p = c.scrollTop / IH;
  c.querySelectorAll<HTMLElement>('li').forEach((li, k) => {
    const d = k - p, a = clamp(d * 26, -80, 80);
    li.style.transform = `rotateX(${(-a).toFixed(1)}deg) translateZ(${(-Math.abs(d) * 3).toFixed(1)}px)`;
    li.style.opacity = Math.max(0.18, 1 - Math.abs(d) * 0.32).toFixed(2);
  });
}

export function EventRangePicker({
  startAt, endAt, isAllDay, onStartChange, onEndChange, onAllDayChange, accent, required, endFieldClassName,
  presets, endLocked = false,
}: EventRangePickerProps) {
  const s = read(startAt);
  const e = read(endAt);
  const S = s.n, E = e.n, sT = s.min, eT = e.min;
  const timeOn = !isAllDay;

  const now = new Date();
  const TODAY = dn(now.getFullYear(), now.getMonth(), now.getDate());
  const TY = now.getFullYear();

  const [open, setOpen] = useState(false);
  const [act, setAct] = useState<Key>('s');
  const [hov, setHov] = useState<number | null>(null);
  const [view, setView] = useState(() => ({ y: pt(S).y, m0: pt(S).m - 1 }));
  const [foc, setFoc] = useState(S);
  const [h24, setH24State] = useState(readH24);
  const [anchor, setAnchor] = useState<Key>('s');
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'down' | 'up' }>({ top: 0, left: 0, placement: 'down' });
  // 시간 말풍선(드럼)
  const [popOn, setPopOn] = useState(false);
  const [popK, setPopK] = useState<Key>('s');
  const [popAt, setPopAt] = useState<{ top: number; right: number }>({ top: 0, right: 100 });
  const [sel, setSel] = useState<[number, number, number]>([0, 9, 0]);
  // 연도 팝업
  const [yOn, setYOn] = useState(false);
  const [yb, setYb] = useState(TY - 4);
  const [yf, setYf] = useState(TY);

  const trig = { s: useRef<HTMLButtonElement>(null), e: useRef<HTMLButtonElement>(null) };
  const anchorRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const ttxRef = useRef<HTMLSpanElement>(null);
  const ttlRef = useRef<HTMLButtonElement>(null);
  const ywRef = useRef<HTMLDivElement>(null);
  const ygRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const fieldRef = { s: useRef<HTMLDivElement>(null), e: useRef<HTMLDivElement>(null) };
  const tbRef = { s: useRef<HTMLButtonElement>(null), e: useRef<HTMLButtonElement>(null) };
  const colRef = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
  // 그린 뒤에 돌릴 모션 · 초점(칸이 새 숫자로 바뀐 뒤에 움직여야 한다).
  const fx = useRef<Array<() => void>>([]);
  // 드럼 스크롤의 rAF · 멈춤 타이머 — 닫거나 사라질 때 모두 푼다.
  const colRaf = useRef<number[]>([0, 0, 0]);
  const colTimer = useRef<number[]>([0, 0, 0]);
  // 열 때 드럼을 지금 시각에 맞추며 낸 scroll 은 «고르기»가 아니다 — 5분 단위가 아닌 시각(하루종일을 끈 23:59 등)이
  //   말풍선을 «열기만 해도» 바뀌지 않게 그 한 번은 기울이기만 한다.
  const colQuiet = useRef<boolean[]>([false, false, false]);
  // 드럼 콜백(타이머 안)은 «지금» 값을 봐야 한다.
  const live = useRef({ S, E, sT, eT, popK, h24, sel, onStartChange, onEndChange });
  live.current = { S, E, sT, eT, popK, h24, sel, onStartChange, onEndChange };

  useEffect(() => {
    const q = fx.current.splice(0);
    q.forEach((f) => f());
  });
  useEffect(() => () => {
    colRaf.current.forEach((r) => { if (r) window.cancelAnimationFrame(r); });
    colTimer.current.forEach((t) => { if (t) window.clearTimeout(t); });
  }, []);

  const locale = getDateLocale();
  const ampmOf = (h: number) => tCalendar(h < 12 ? 'calendar.picker.am' : 'calendar.picker.pm');
  const fmtTime = (v: number) => {
    const h = Math.floor(v / 60), m = v % 60;
    return h24 ? `${pad(h)}:${pad(m)}` : `${ampmOf(h)} ${displayHour12(h)}:${pad(m)}`;
  };
  const fmtFull = (n: number) => localDate(n).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  /*
   * ⭐ 2026-10-20 — 날짜 칸(시안 B · 사용자 결정): 위에 「날짜」 이름표, 상자 안에 「2026년 10월 20일 (화) │ 오후 12:00 – 오후 1:00 ⌄」.
   *   (그 전 판: 노션식 한 줄 — 덩그러니 보이고 «고칠 수 있다»는 표시가 없었다.)
   *   시작 덩어리 = 시작 날짜 │ 시작 시각, 종료 덩어리 = (같은 날이면) 종료 시각 · (다른 날이면) 종료 날짜 + 시각.
   *   하루종일이면 시각 없이 날짜만, 하루짜리 하루종일이면 종료 덩어리를 숨긴다.
   */
  const sameDay = S === E;
  const sameYear = pt(S).y === pt(E).y;
  const wd = (n: number) => localDate(n).toLocaleDateString(locale, { weekday: 'short' });
  const dateLong = (n: number) => `${fmtFull(n)} (${wd(n)})`;
  const dateShort = (n: number) => `${localDate(n).toLocaleDateString(locale, sameYear ? { month: 'long', day: 'numeric' } : { year: 'numeric', month: 'long', day: 'numeric' })} (${wd(n)})`;
  const triggerText = (k: Key) => {
    if (k === 's') return isAllDay ? dateLong(S) : `${dateLong(S)} ${fmtTime(sT)}`;
    if (isAllDay) return dateShort(E);
    return sameDay ? fmtTime(eT) : `${dateShort(E)} ${fmtTime(eT)}`;
  };
  const endHidden = isAllDay && sameDay;

  // ── 앵커 창 자리: 누른 칸 아래, 모자라면 위, 화면 안으로 clamp (기존 피커와 같은 규칙) ──
  useLayoutEffect(() => {
    if (!open) return;
    const t = trig[anchor].current;
    const el = anchorRef.current;
    if (!t || !el) return;
    const r = t.getBoundingClientRect();
    const w = el.offsetWidth, h = el.offsetHeight, margin = 8;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = r.left;
    if (left + w + margin > vw) left = vw - w - margin;
    if (left < margin) left = margin;
    let top = r.bottom + 6;
    let placement: 'down' | 'up' = 'down';
    if (top + h + margin > vh) {
      top = Math.max(margin, r.top - h - 6);
      placement = 'up';
    }
    setPos({ top, left, placement });
  }, [open, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 달 넘기기 ──
  function changeView(y: number, m0: number, dir: number) {
    const t = new Date(Date.UTC(y, m0, 1));
    const ny = t.getUTCFullYear(), nm = t.getUTCMonth();
    setView({ y: ny, m0: nm });
    const first = dn(ny, nm, 1), last = dn(ny, nm + 1, 0);
    const inV = (n: number | null) => n != null && n >= first && n <= last;
    setFoc((f) => (inV(f) ? f : inV(S) ? S : inV(TODAY) ? TODAY : first));
    if (dir) {
      fx.current.push(() => {
        play(gridRef.current, [{ transform: `translateX(${dir * 18}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], 320);
        play(ttxRef.current, [{ transform: `translateY(${dir * 4}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], 260);
      });
    }
  }
  const dirTo = (y: number, m0: number) => Math.sign(y * 12 + m0 - (view.y * 12 + view.m0));
  function showMonthOf(n: number) {
    const o = pt(n), d = dirTo(o.y, o.m - 1);
    if (d) changeView(o.y, o.m - 1, d);
  }

  const flash = (k: Key) => fx.current.push(() =>
    play(fieldRef[k].current?.querySelector('.u914-fx'), [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], 260));
  const pop1 = (n: number) => fx.current.push(() =>
    play(gridRef.current?.querySelector(`.u914-d[data-n="${n}"] span`), [{ transform: 'scale(.7)' }, { transform: 'scale(1.08)', offset: 0.6 }, { transform: 'none' }], 380));
  const focusLater = (get: () => HTMLElement | null | undefined) => fx.current.push(() => get()?.focus({ preventScroll: true }));

  // ── 열기 · 닫기 ──
  function openAt(k: Key) {
    const n = k === 's' ? S : E;
    setAct(endLocked ? 's' : k);
    setAnchor(k);
    setHov(null);
    setFoc(n);
    setView({ y: pt(n).y, m0: pt(n).m - 1 });
    setOpen(true);
    focusLater(() => gridRef.current?.querySelector<HTMLElement>(`.u914-d[data-n="${n}"]`));
  }
  function close(returnFocus: boolean) {
    setPopOn(false);
    setYOn(false);
    setOpen(false);
    setHov(null);
    if (returnFocus) trig[anchor].current?.focus({ preventScroll: true });
  }
  function onTrigger(k: Key) {
    if (!open) { openAt(k); return; }
    if (act === k || (endLocked && k === 'e')) { close(false); return; }
    setAct(k);
    showMonthOf(k === 's' ? S : E);
  }

  // 바깥 클릭 · Esc — Esc 는 안쪽부터 하나씩 닫는다(연도 팝업 → 시간 말풍선 → 달력 창). 일정 창까지는 닫지 않는다.
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      const t = ev.target as Node;
      if (anchorRef.current?.contains(t) || trig.s.current?.contains(t) || trig.e.current?.contains(t)) return;
      close(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      ev.stopPropagation();
      if (yOn) closeYr(true);
      else if (popOn) closePop(true);
      else close(true);
    }
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey, true); };
  }); // 매번 다시 건다 — 닫기가 지금 값(열린 팝업·anchor)을 봐야 한다

  // ── 날짜 고르기: 시작 → 종료 순서로 번갈아. 시작보다 앞을 종료로 고르면 서로 바꾼다 ──
  function pick(n: number) {
    setFoc(n);
    showMonthOf(n);
    if (endLocked) {
      onStartChange(write(n, sT));
      setAct('s');
      flash('s'); flash('e');
    } else if (act === 's') {
      onStartChange(write(n, sT));
      setAct('e');
      flash('s');
      if (E < n) flash('e');
    } else if (n < S) {
      onStartChange(write(n, sT));
      onEndChange(write(S, eT));
      setAct('s');
      flash('s'); flash('e');
    } else {
      onEndChange(write(n, eT));
      setAct('s');
      flash('e');
    }
    setHov(null);
    pop1(n);
  }

  const STEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
  function onGridKey(ev: React.KeyboardEvent) {
    const k = ev.key;
    let n = foc;
    if (Object.prototype.hasOwnProperty.call(STEP, k)) n += STEP[k] ?? 0;
    else if (k === 'Home') n -= pt(n).w;
    else if (k === 'End') n += 6 - pt(n).w;
    else if (k === 'PageUp' || k === 'PageDown') {
      const o = pt(n);
      const t = new Date(Date.UTC(o.y, o.m - 1 + (k === 'PageUp' ? -1 : 1), 1));
      const y = t.getUTCFullYear(), m = t.getUTCMonth();
      n = Math.min(dn(y, m, o.d), dn(y, m + 1, 0));
    } else return;
    ev.preventDefault();
    setFoc(n);
    if (act === 'e') setHov(n);
    showMonthOf(n);
    focusLater(() => gridRef.current?.querySelector<HTMLElement>(`.u914-d[data-n="${n}"]`));
  }

  // ── 연도 팝업: 제목을 누르면 12개 연도가 3열로 펼쳐지고, 고르면 같은 달의 그 해로 넘어간다 ──
  const yFocus = (y: number) => focusLater(() => ygRef.current?.querySelector<HTMLElement>(`.u914-yc[data-y="${y}"]`));
  const ySlide = (dir: number) => fx.current.push(() =>
    play(ygRef.current, [{ transform: `translateX(${dir * 16}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], 300));
  function openYr() {
    setPopOn(false);
    const f = clamp(view.y, YMIN, YMAX);
    setYf(f);
    setYb(clamp(f - 4, YMIN, YMAX - 11));
    setYOn(true);
    yFocus(f);
  }
  function closeYr(ret: boolean) {
    if (!yOn) return;
    setYOn(false);
    if (ret) ttlRef.current?.focus({ preventScroll: true });
  }
  function yPage(d: number, keepFocus = false) {
    const nb = clamp(yb + d * 12, YMIN, YMAX - 11);
    if (nb === yb) return;
    const nf = clamp(yf + (nb - yb), nb, nb + 11);
    setYb(nb);
    setYf(nf);
    ySlide(d);
    if (keepFocus) yFocus(nf);
  }
  function yMove(y: number) {
    const ny = clamp(y, YMIN, YMAX);
    let d = 0, nb = yb;
    if (ny < yb) { d = -1; nb = clamp(yb - 12, YMIN, YMAX - 11); }
    else if (ny > yb + 11) { d = 1; nb = clamp(yb + 12, YMIN, YMAX - 11); }
    setYb(nb);
    setYf(ny);
    if (d) ySlide(d);
    yFocus(ny);
  }
  function yPick(y: number) {
    closeYr(true);
    const d = Math.sign(y - view.y);
    if (d) changeView(y, view.m0, d);
    else fx.current.push(() => play(ttxRef.current, [{ transform: 'scale(.94)' }, { transform: 'none' }], 260));
  }
  const YSTEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 };
  function onYearKey(ev: React.KeyboardEvent) {
    const k = ev.key;
    if (Object.prototype.hasOwnProperty.call(YSTEP, k)) yMove(yf + (YSTEP[k] ?? 0));
    else if (k === 'Home') yMove(yb);
    else if (k === 'End') yMove(yb + 11);
    else if (k === 'PageUp' || k === 'PageDown') yPage(k === 'PageUp' ? -1 : 1, true);
    else return;
    ev.preventDefault();
  }

  // ── 시간 말풍선(드럼) ──
  const apList = [tCalendar('calendar.picker.am'), tCalendar('calendar.picker.pm')];
  const hourList = h24 ? Array.from({ length: 24 }, (_, i) => pad(i)) : Array.from({ length: 12 }, (_, i) => String(i || 12));
  const minList = Array.from({ length: 12 }, (_, i) => pad(i * 5));
  const lists = [apList, hourList, minList];
  const toSel = (v: number, is24: boolean): [number, number, number] => {
    const h = Math.floor(v / 60);
    return [h >= 12 ? 1 : 0, is24 ? h : h % 12, Math.round((v % 60) / 5) % 12];
  };
  const fromSel = (x: [number, number, number], is24: boolean) => (is24 ? x[1] : x[1] + x[0] * 12) * 60 + x[2] * 5;

  function openPop(k: Key) {
    if (!timeOn) return;
    setYOn(false);
    const again = popOn;
    setPopK(k);
    setAct(k);
    setSel(toSel(k === 's' ? sT : eT, h24));
    setPopOn(true);
    if (again) fx.current.push(() => play(popRef.current, [{ opacity: 0.4, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }], 260));
    focusLater(() => colRef[1]?.current);
  }
  function closePop(ret: boolean) {
    if (!popOn) return;
    setPopOn(false);
    if (ret) tbRef[popK].current?.focus({ preventScroll: true });
  }
  // 말풍선은 누른 시간 칸 바로 왼쪽에 붙고, 꼬리가 그 시간을 가리킨다(원본 place).
  // 열 때 드럼을 지금 시각으로 맞춘다 — scrollTop 을 옮기면 scroll 이 나지만, 같은 칸이라 값은 그대로다.
  useLayoutEffect(() => {
    if (!popOn) return;
    const root = rootRef.current, f = fieldRef[popK].current, tb = tbRef[popK].current;
    if (root && f && tb) setPopAt({ top: f.offsetTop - 6, right: root.clientWidth - (f.offsetLeft + f.offsetWidth) + tb.offsetWidth + 10 });
    colRef.forEach((r, ci) => {
      const c = r.current;
      if (!c || (ci === 0 && h24)) return;
      const top = (sel[ci] ?? 0) * IH;
      if (c.scrollTop !== top) colQuiet.current[ci] = true;
      c.scrollTop = top;
      bend(c);
    });
  }, [popOn, popK, h24]); // eslint-disable-line react-hooks/exhaustive-deps

  // 굴림이 멈추면(120ms) 가운데 칸을 고른다 — 그때 시작/종료 시각을 바로 바꾼다.
  function settle(ci: number) {
    const c = colRef[ci]?.current;
    const L = live.current;
    if (!c || (ci === 0 && L.h24)) return;
    const n = c.querySelectorAll('li').length;
    if (!n) return;
    const k = clamp(Math.round(c.scrollTop / IH), 0, n - 1);
    const next = [...L.sel] as [number, number, number];
    next[ci] = k;
    setSel(next);
    const v = fromSel(next, L.h24);
    const cur = L.popK === 's' ? L.sT : L.eT;
    if (v === cur) return;
    if (L.popK === 's') L.onStartChange(write(L.S, v)); else L.onEndChange(write(L.E, v));
  }
  function onColScroll(ci: number) {
    const c = colRef[ci]?.current;
    if (!c) return;
    if (!colRaf.current[ci]) {
      colRaf.current[ci] = window.requestAnimationFrame(() => { colRaf.current[ci] = 0; bend(c); });
    }
    if (colQuiet.current[ci]) { colQuiet.current[ci] = false; return; }
    window.clearTimeout(colTimer.current[ci]);
    colTimer.current[ci] = window.setTimeout(() => { colTimer.current[ci] = 0; settle(ci); }, 120);
  }
  function go(ci: number, k: number) {
    const c = colRef[ci]?.current;
    if (!c) return;
    const top = clamp(k, 0, c.querySelectorAll('li').length - 1) * IH;
    if (typeof c.scrollTo === 'function') c.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    else c.scrollTop = top;
  }
  function onColKey(ci: number, ev: React.KeyboardEvent<HTMLDivElement>) {
    const c = ev.currentTarget;
    const k = Math.round(c.scrollTop / IH);
    const vis = colRef.map((r) => r.current).filter((x, i): x is HTMLDivElement => !!x && !(i === 0 && h24));
    const vi = vis.indexOf(c);
    if (ev.key === 'ArrowDown') go(ci, k + 1);
    else if (ev.key === 'ArrowUp') go(ci, k - 1);
    else if (ev.key === 'Home') go(ci, 0);
    else if (ev.key === 'End') go(ci, c.querySelectorAll('li').length - 1);
    else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
      vis[vi + (ev.key === 'ArrowRight' ? 1 : -1)]?.focus({ preventScroll: true });
    } else if (ev.key === 'Enter') closePop(true);
    else return;
    ev.preventDefault();
  }

  function setFmt(v: boolean) {
    if (v === h24) return;
    setPopOn(false);
    setH24State(v);
    saveH24(v);
    fx.current.push(() => (['s', 'e'] as Key[]).forEach((k) =>
      play(tbRef[k].current, [{ opacity: 0, transform: 'translateY(3px)' }, { opacity: 1, transform: 'none' }], 260)));
  }

  // ── 달력 칸 42개 ──
  const first = dn(view.y, view.m0, 1);
  const c0 = first - pt(first).w;
  const prev = act === 'e' && hov != null && hov >= S;
  const bandEnd = prev ? hov! : E;
  const band = bandEnd > S;
  const monthTitle = localDate(first).toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((d) => tCalendar(`calendar.month.dayname.${d}`));

  const status = `${fmtFull(S)}${timeOn ? ` ${fmtTime(sT)}` : ''} → ${fmtFull(E)}${timeOn ? ` ${fmtTime(eT)}` : ''}`;
  const accentColor = accent ?? 'var(--primary)';
  const timeLabel = (k: Key) => tCalendar(k === 's' ? 'calendar.range.start_time' : 'calendar.range.end_time');

  const trigger = (k: Key) => {
    const isOpen = open && act === k;
    const text = triggerText(k);
    return (
      <button
        type="button"
        ref={trig[k]}
        id={k === 's' ? 'event-start' : 'event-end'}
        className={`cal-datebox-btn${isOpen ? ' is-open' : ''}`}
        onClick={(ev) => { ev.stopPropagation(); onTrigger(k); }}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-required={required}
        aria-label={`${tCalendar(k === 's' ? 'calendar.event_create.label_start' : 'calendar.event_create.label_end')} ${text}`}
        hidden={k === 'e' && endHidden}
      >
        {k === 's' ? (
          <>
            <span>{dateLong(S)}</span>
            {!isAllDay && <span className="cal-datebox-div" aria-hidden="true" />}
            {!isAllDay && <span>{fmtTime(sT)}</span>}
          </>
        ) : text}
      </button>
    );
  };

  const field = (k: Key) => {
    const n = k === 's' ? S : E;
    const tt = fmtTime(k === 's' ? sT : eT);
    const locked = endLocked && k === 'e';
    return (
      <div ref={fieldRef[k]} className={`u914-f${act === k ? ' is-act' : ''}${locked ? ' is-lock' : ''}`} data-k={k} title={locked ? tCalendar('calendar.preset.end_locked') : undefined}>
        <button
          type="button"
          className="u914-fd"
          aria-pressed={act === k}
          disabled={locked}
          onClick={() => { setAct(k); setFoc(n); showMonthOf(n); }}
        >
          <span className="u914-fk">{tCalendar(k === 's' ? 'calendar.event_create.label_start' : 'calendar.event_create.label_end')}</span>
          <span className="u914-fx">{fmtFull(n)}</span>
        </button>
        <span className="u914-tw">
          <button
            ref={tbRef[k]}
            type="button"
            className="u914-tb"
            aria-haspopup="dialog"
            aria-expanded={popOn && popK === k}
            aria-label={`${timeLabel(k)} ${tt}`}
            aria-hidden={!timeOn}
            tabIndex={timeOn && !locked ? 0 : -1}
            disabled={locked}
            onClick={() => { if (popOn && popK === k) closePop(false); else openPop(k); }}
            onKeyDown={(ev) => { if (ev.key === 'ArrowDown') { ev.preventDefault(); openPop(k); } }}
          >
            {tt}
          </button>
        </span>
      </div>
    );
  };

  const yearCells = Array.from({ length: 12 }, (_, i) => yb + i);

  return (
    <>
      {/* ⭐ 2026-10-20 — 시안 B: 「날짜」 이름표 + 상자(다른 칸과 같은 모양 · ⌄). 상자 빈 곳을 누르면 시작을 고른다. */}
      <div className={`calendar-modal-field cal-datefield${endFieldClassName ? ` ${endFieldClassName}` : ''}`}>
        <label htmlFor="event-start">{tCalendar('calendar.range.date_label')}</label>
        <div
          className={`cal-datebox${open ? ' is-open' : ''}`}
          style={{ '--dtp-accent': accentColor } as React.CSSProperties}
          onClick={() => onTrigger('s')}
        >
          {trigger('s')}
          {!endHidden && <span className="cal-datebox-dash" aria-hidden="true">–</span>}
          {trigger('e')}
          <ChevronDown className="cal-datebox-chev" size={16} aria-hidden />
        </div>
      </div>

      {open && (
        <div
          ref={anchorRef}
          className={`u914-anchor u914-anchor--${pos.placement}`}
          style={{ top: pos.top, left: pos.left }}
          role="dialog"
          aria-label={tCalendar('calendar.picker.aria')}
        >
          <div
            ref={rootRef}
            className={`u914 is-end${timeOn ? ' is-time' : ''}${h24 ? ' is-24' : ''}${prev ? ' is-prev' : ''}${popOn ? ' is-pop' : ''}${yOn ? ' is-yr' : ''}`}
            role="group"
            aria-label={tCalendar('calendar.picker.aria')}
            onClick={(ev) => {
              const t = ev.target as HTMLElement;
              if (popOn && !popRef.current?.contains(t) && !t.closest('.u914-tb')) closePop(false);
              if (yOn && !ywRef.current?.contains(t)) closeYr(false);
            }}
          >
            <div className="u914-fs">
              {field('s')}
              <div className="u914-ew"><div className="u914-ewi"><div className="u914-ewp">{field('e')}</div></div></div>
            </div>

            {/* ⭐ 2026-10-20 — 유형별 추천 칩(시안 E). 회의 = 길이 · 외부 = 오전/오후 · 휴가 = 반차/반반차/연차. */}
            {presets && presets.chips.length > 0 && (
              <div className="u914-rec" role="group" aria-label={presets.label}>
                <span className="u914-rl" aria-hidden="true">{presets.label}</span>
                {presets.chips.map((c) => (
                  <button key={c.key} type="button" className="u914-chip" aria-pressed={c.on} onClick={() => { setPopOn(false); c.onPick(); }}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            <div
              ref={popRef}
              className="u914-pop"
              role="dialog"
              aria-label={timeLabel(popK)}
              aria-hidden={!popOn}
              style={{ top: popAt.top, right: popAt.right }}
              onBlur={(ev) => {
                const t = ev.relatedTarget as HTMLElement | null;
                if (popOn && t && !popRef.current?.contains(t) && !t.closest?.('.u914-tb')) closePop(false);
              }}
            >
              <div className="u914-ph">
                <span className="u914-pl">{timeLabel(popK)}</span>
                <button type="button" className="u914-ok" tabIndex={popOn ? 0 : -1} onClick={() => closePop(true)}>{tCalendar('calendar.range.done')}</button>
              </div>
              <div className="u914-pk">
                <span className="u914-band" aria-hidden="true" />
                {lists.map((list, ci) => (
                  <div
                    key={ci}
                    ref={colRef[ci]}
                    className={`u914-col${ci === 0 ? ' u914-ap' : ''}`}
                    tabIndex={popOn ? 0 : -1}
                    role="listbox"
                    aria-label={tCalendar(ci === 0 ? 'calendar.picker.ampm' : ci === 1 ? 'calendar.picker.hour' : 'calendar.picker.minute')}
                    onScroll={() => onColScroll(ci)}
                    onKeyDown={(ev) => onColKey(ci, ev)}
                    onClick={(ev) => {
                      const li = (ev.target as HTMLElement).closest<HTMLElement>('li');
                      if (li) go(ci, Number(li.dataset.i));
                    }}
                  >
                    <ul>
                      {list.map((t, i) => (
                        <li key={`${h24}-${i}`} data-i={i} role="option" aria-selected={sel[ci] === i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="u914-ch">
              <div className="u914-yw" ref={ywRef}>
                <button
                  ref={ttlRef}
                  type="button"
                  className="u914-ttl"
                  aria-haspopup="dialog"
                  aria-expanded={yOn}
                  onClick={() => { if (yOn) closeYr(true); else openYr(); }}
                  onKeyDown={(ev) => { if (ev.key === 'ArrowDown' && !yOn) { ev.preventDefault(); openYr(); } }}
                >
                  <span className="u914-tt" ref={ttxRef} aria-live="polite">{monthTitle}</span>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4.5 6.5 8 10l3.5-3.5" /></svg>
                </button>
                <div
                  className="u914-yp"
                  role="dialog"
                  aria-label={tCalendar('calendar.range.year_pick')}
                  aria-hidden={!yOn}
                  onBlur={(ev) => {
                    const t = ev.relatedTarget as Node | null;
                    if (yOn && t && !ywRef.current?.contains(t)) closeYr(false);
                  }}
                >
                  <div className="u914-yh">
                    <button type="button" className="u914-yv" aria-label={tCalendar('calendar.range.prev_years')} disabled={yb <= YMIN} tabIndex={yOn ? 0 : -1} onClick={() => yPage(-1)}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" /></svg>
                    </button>
                    <b className="u914-yr" aria-hidden="true">{`${yb} – ${yb + 11}`}</b>
                    <button type="button" className="u914-yn" aria-label={tCalendar('calendar.range.next_years')} disabled={yb >= YMAX - 11} tabIndex={yOn ? 0 : -1} onClick={() => yPage(1)}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3.5 10.5 8 6 12.5" /></svg>
                    </button>
                  </div>
                  <div className="u914-yg" role="group" aria-label={tCalendar('calendar.range.year')} ref={ygRef} onKeyDown={onYearKey}>
                    {yearCells.map((y) => (
                      <button
                        key={y}
                        type="button"
                        className={`u914-yc${y === TY ? ' is-ty' : ''}`}
                        data-y={y}
                        aria-pressed={y === view.y}
                        aria-current={y === TY ? 'date' : undefined}
                        tabIndex={yOn && y === yf ? 0 : -1}
                        onClick={() => yPick(y)}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="u914-today"
                onClick={() => {
                  setFoc(TODAY);
                  const d = dirTo(now.getFullYear(), now.getMonth());
                  if (d) changeView(now.getFullYear(), now.getMonth(), d); else pop1(TODAY);
                }}
              >
                {tCalendar('calendar.nav.today')}
              </button>
              <button type="button" className="u914-pv" aria-label={tCalendar('calendar.nav.prev_month')} onClick={() => changeView(view.y, view.m0 - 1, -1)}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" /></svg>
              </button>
              <button type="button" className="u914-nx" aria-label={tCalendar('calendar.nav.next_month')} onClick={() => changeView(view.y, view.m0 + 1, 1)}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3.5 10.5 8 6 12.5" /></svg>
              </button>
            </div>
            <div className="u914-wk" aria-hidden="true">{dayNames.map((d) => <span key={d}>{d}</span>)}</div>
            <div className="u914-gv">
              <div
                className="u914-g"
                role="group"
                aria-label={monthTitle}
                ref={gridRef}
                onKeyDown={onGridKey}
                onMouseOver={(ev) => {
                  const c = (ev.target as HTMLElement).closest<HTMLElement>('.u914-d');
                  const n = c ? Number(c.dataset.n) : null;
                  if (n !== hov) setHov(n);
                }}
                onMouseLeave={() => { if (hov != null) setHov(null); }}
              >
                {Array.from({ length: 42 }, (_, i) => {
                  const n = c0 + i, o = pt(n);
                  const cls = [
                    'u914-d',
                    o.m - 1 !== view.m0 ? 'is-out' : '',
                    n === TODAY ? 'is-td' : '',
                    n === S ? 'is-s' : '',
                    !prev && n === E ? 'is-e' : '',
                    prev && n === hov && n !== S ? 'is-he' : '',
                    band && n >= S && n <= bandEnd ? 'is-in' : '',
                    band && n === S ? 'is-rs' : '',
                    band && n === bandEnd ? 'is-re' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cls}
                      data-n={n}
                      tabIndex={n === foc ? 0 : -1}
                      aria-label={localDate(n).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
                      aria-pressed={n === S || n === E}
                      aria-current={n === TODAY ? 'date' : undefined}
                      onClick={() => pick(n)}
                    >
                      <span>{o.d}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="u914-hr" aria-hidden="true" />
            <button type="button" className="u914-row u914-sw" role="switch" aria-checked={isAllDay} onClick={() => { setPopOn(false); onAllDayChange(!isAllDay); }}>
              <span>{tCalendar('calendar.event_create.label_allday')}</span><i className="u914-tg" aria-hidden="true" />
            </button>
            <div className="u914-row u914-rt">
              <span>{tCalendar('calendar.range.time_format')}</span>
              <div
                className={`u914-seg${timeOn ? '' : ' is-off'}`}
                role="radiogroup"
                aria-label={tCalendar('calendar.range.time_format')}
                onKeyDown={(ev) => {
                  if (!/^Arrow/.test(ev.key)) return;
                  ev.preventDefault();
                  const v = ev.key === 'ArrowRight' || ev.key === 'ArrowDown';
                  setFmt(v);
                  (ev.currentTarget.querySelectorAll<HTMLButtonElement>('.u914-tf')[v ? 1 : 0])?.focus({ preventScroll: true });
                }}
              >
                <i aria-hidden="true" />
                {[false, true].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    className="u914-tf"
                    role="radio"
                    aria-checked={h24 === v}
                    disabled={!timeOn}
                    tabIndex={h24 === v ? 0 : -1}
                    onClick={() => setFmt(v)}
                  >
                    {tCalendar(v ? 'calendar.range.h24' : 'calendar.range.h12')}
                  </button>
                ))}
              </div>
            </div>
            <div className="u914-hr" aria-hidden="true" />
            <button type="button" className="u914-row u914-clr" onClick={() => close(true)}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="8" cy="8" r="6.2" /><path d="M5.4 8.2 7.2 10l3.4-3.8" /></svg>
              <span>{tCalendar('calendar.picker.confirm')}</span>
            </button>
            <div className="u914-sr" role="status">{status}</div>
          </div>
        </div>
      )}
    </>
  );
}
